import type { ReactNode } from 'react'
import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import goleroLogo from '@/assets/images/golero-logo.svg'
import { useAuth } from '@/auth/AuthContext'
import { LanguagePicker } from '@/components/LanguagePicker'
import { CelebrationBootstrap } from '@/features/celebrations/CelebrationBootstrap'
import { getDisplayLabel, useCurrentUser } from '@/features/user/api'
import { usePouleHomeSummary } from '@/features/home/api'
import { setPreferredLocale, syncDocumentLocale, type AppLocale } from '@/config/locale'
import { translate, useDomTranslations, useTranslation } from '@/i18n'
import { useTheme } from '@/theme/ThemeProvider'

const appNavItems = [
  { to: '/dashboard', label: 'Home', end: true, external: false },
  { to: '/dashboard/poules', label: 'Voetbalpoules', end: false, external: false },
  { to: '/dashboard/manager', label: 'Golero Manager', end: false, external: false },
  { to: '/dashboard/trivia', label: 'Golero Trivia', end: false, external: false },
  { to: '/squad-builder', label: 'Squad Builder', end: false, external: true },
] as const

export function AppLayout() {
  useDomTranslations()
  const { user, signOut } = useAuth()
  const { theme, toggleTheme } = useTheme()
  const { locale: activeLocale } = useTranslation()
  const queryClient = useQueryClient()
  const previousLocaleRef = useRef(activeLocale)
  const navigate = useNavigate()
  const location = useLocation()
  const currentUserQuery = useCurrentUser(Boolean(user))
  usePouleHomeSummary(activeLocale, Boolean(user && currentUserQuery.data))
  const [, refreshForLocale] = useState(0)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const userLabel = currentUserQuery.data
    ? getDisplayLabel(currentUserQuery.data)
    : user?.displayName?.trim() || translate('Speler')
  const isAdmin = Boolean(currentUserQuery.data?.is_admin)

  // Keep the outlet in sync when the language selector changes.
  void activeLocale

  useEffect(() => {
    if (previousLocaleRef.current === activeLocale) return
    previousLocaleRef.current = activeLocale
    void queryClient.invalidateQueries()
  }, [activeLocale, queryClient])

  useEffect(() => {
    if (!currentUserQuery.data?.preferred_locale) return
    if (window.localStorage.getItem('golero_locale')) return
    if (!setPreferredLocale(currentUserQuery.data.preferred_locale)) return
    syncDocumentLocale()
    refreshForLocale((value) => value + 1)
  }, [currentUserQuery.data?.preferred_locale])

  useEffect(() => {
    setIsMobileMenuOpen(false)
  }, [location.pathname, location.search, location.hash])

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  }, [location.pathname, location.search])

  useEffect(() => {
    const previousOverflow = document.body.style.overflow
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsMobileMenuOpen(false)
      }
    }

    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden'
      window.addEventListener('keydown', handleKeyDown)
    }

    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isMobileMenuOpen])

  async function handleLogout() {
    await signOut()
    navigate('/login', { replace: true })
  }

  return (
    <div className="app-shell relative flex min-h-screen flex-col bg-[#02060c] text-surface-100">
      <CelebrationBootstrap userId={location.pathname === '/dashboard' ? null : user?.uid ?? null} />
      <div className="app-backdrop-grid pointer-events-none fixed inset-0 z-0" />
      <div className="app-backdrop-glow pointer-events-none fixed inset-0 z-0" />

      <div className="relative z-10 flex min-h-screen flex-col">
      <DesktopHeader userLabel={userLabel} isAdmin={isAdmin} onLogout={handleLogout} theme={theme} onToggleTheme={toggleTheme} />
      <MobileHeader
        isMenuOpen={isMobileMenuOpen}
        onToggleMenu={() => setIsMobileMenuOpen((open) => !open)}
        theme={theme}
        onToggleTheme={toggleTheme}
      />
      <MobileMenu
        userLabel={userLabel}
        isAdmin={isAdmin}
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        onLogout={handleLogout}
      />

      <main className="mx-auto w-full max-w-[1000px] flex-1 px-4 pb-5 pt-24 sm:pt-[7.35rem]">
        <AppBreadcrumbs />
        <Outlet />
      </main>

      <AppFooter />
      </div>
    </div>
  )
}

const IOS_STORE_URL = 'https://apps.apple.com/us/app/match-o/id6754211570'
const ANDROID_STORE_URL = 'https://play.google.com/store/apps/details?id=nl.mumba.matcho.app'
const MARKETING_ASSET_BASE_URL = 'https://golero.nl/marketing-assets'
const PUBLIC_LEGAL_LINKS: Record<AppLocale, { terms: string; privacy: string }> = {
  nl: {
    terms: 'https://golero.nl/nl/algemene-voorwaarden',
    privacy: 'https://golero.nl/nl/privacy-statement',
  },
  en: {
    terms: 'https://golero.nl/en/terms-of-service',
    privacy: 'https://golero.nl/en/privacy-statement',
  },
  fr: {
    terms: 'https://golero.nl/fr/conditions-generales',
    privacy: 'https://golero.nl/fr/politique-de-confidentialite',
  },
  es: {
    terms: 'https://golero.nl/es/terminos-del-servicio',
    privacy: 'https://golero.nl/es/declaracion-de-privacidad',
  },
  de: {
    terms: 'https://golero.nl/de/nutzungsbedingungen',
    privacy: 'https://golero.nl/de/datenschutzerklaerung',
  },
}

function ThemeToggle({ theme, onToggle }: { theme: 'dark' | 'light'; onToggle: () => void }) {
  const { t } = useTranslation()
  const isLight = theme === 'light'

  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={t(isLight ? 'Donkermodus inschakelen' : 'Lichtmodus inschakelen')}
      title={t(isLight ? 'Donkermodus inschakelen' : 'Lichtmodus inschakelen')}
      className="theme-toggle inline-flex h-7 w-7 items-center justify-center rounded-full text-surface-400 transition hover:bg-white/[0.06] hover:text-white"
    >
      {isLight ? (
        <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" aria-hidden="true">
          <path d="M16.5 12.6A6.8 6.8 0 0 1 7.4 3.5a6.8 6.8 0 1 0 9.1 9.1Z" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
        </svg>
      ) : (
        <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" aria-hidden="true">
          <circle cx="10" cy="10" r="3.2" stroke="currentColor" strokeWidth="1.5" />
          <path d="M10 2v1.6M10 16.4V18M2 10h1.6M16.4 10H18M4.35 4.35l1.1 1.1M14.55 14.55l1.1 1.1M15.65 4.35l-1.1 1.1M5.45 14.55l-1.1 1.1" stroke="currentColor" strokeLinecap="round" strokeWidth="1.5" />
        </svg>
      )}
    </button>
  )
}

function AppFooter() {
  const { locale, t } = useTranslation()
  const legalLinks = PUBLIC_LEGAL_LINKS[locale]

  return (
    <footer className="app-footer mt-16 border-t border-white/[0.07] bg-[#05080b]">
      <div className="mx-auto w-full max-w-[1000px] px-4">
        <div className="relative isolate overflow-hidden border-b border-white/[0.07] py-10 sm:py-12">
          <div className="relative grid gap-8 sm:grid-cols-2 lg:grid-cols-4 lg:items-start">
            <div>
              <Link to="/dashboard" aria-label={t('Golero home')}>
                <img src={goleroLogo} alt="Golero" className="app-logo h-auto w-32" />
              </Link>
              <p className="mt-4 max-w-xs text-sm leading-6 text-surface-500">
                {t('Voorspel wedstrijden, volg je ranking en beheer je Manager-team in Golero.')}
              </p>
            </div>

            <div>
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-surface-300">{t('Download de app')}</p>
              <p className="mt-2 max-w-xs text-sm leading-6 text-surface-500">
                {t('Speel overal verder met Golero op je telefoon.')}
              </p>
              <div className="mt-4 flex flex-nowrap items-center gap-2">
                <a href={IOS_STORE_URL} target="_blank" rel="noreferrer" aria-label={t('Download Golero in de App Store')} className="min-w-0 flex-1">
                  <img src={`${MARKETING_ASSET_BASE_URL}/appstore.svg`} alt={t('Download in de App Store')} className="footer-store-badge h-9 w-full object-contain object-left" />
                </a>
                <a href={ANDROID_STORE_URL} target="_blank" rel="noreferrer" aria-label={t('Download Golero via Google Play')} className="min-w-0 flex-1">
                  <img src={`${MARKETING_ASSET_BASE_URL}/playstore.svg`} alt={t('Download via Google Play')} className="footer-store-badge h-9 w-full object-contain object-left" />
                </a>
              </div>
            </div>

            <nav className="flex flex-col gap-3 text-sm font-semibold text-surface-500" aria-label={t('Footer')}>
              <Link to="/dashboard" className="transition hover:text-white">{t('Home')}</Link>
              <Link to="/dashboard/poules" className="transition hover:text-white">{t('Voetbalpoules')}</Link>
              <Link to="/dashboard/manager" className="transition hover:text-white">{t('Golero Manager')}</Link>
              <Link to="/profiel" className="transition hover:text-white">{t('Profiel')}</Link>
            </nav>

            <nav className="flex flex-col gap-3 text-sm font-semibold text-surface-500" aria-label={t('Juridische informatie')}>
              <a href={legalLinks.terms} target="_blank" rel="noreferrer" className="transition hover:text-white">{t('Algemene voorwaarden')}</a>
              <a href={legalLinks.privacy} target="_blank" rel="noreferrer" className="transition hover:text-white">{t('Privacybeleid')}</a>
              <Link to="/spelregels/football_poule" className="transition hover:text-white">{t('Spelregels voetbalpoules')}</Link>
              <Link to="/spelregels/fantasy" className="transition hover:text-white">{t('Spelregels Golero Manager')}</Link>
              <Link to="/spelregels/quiz_one_vs_one" className="transition hover:text-white">{t('Spelregels Golero Trivia')}</Link>
            </nav>
          </div>
        </div>

        <div className="flex items-center justify-between gap-4 py-5 text-xs text-surface-600">
          <span>© {new Date().getFullYear()} Golero</span>
          <span>{t('Voetbal beleef je samen.')}</span>
        </div>
      </div>
    </footer>
  )
}

function AppBreadcrumbs() {
  const { pathname } = useLocation()
  const { t } = useTranslation()
  const items = getBreadcrumbItems(pathname)

  if (items.length === 0) return null

  return (
    <nav
      aria-label={t('Kruimelpad')}
      className="relative left-1/2 mb-5 w-screen -translate-x-1/2 pb-4 text-xs text-surface-500 after:pointer-events-none after:absolute after:bottom-0 after:left-0 after:right-0 after:border-b after:border-white/[0.08]"
    >
      <div className="mx-auto flex max-w-[1000px] items-center gap-2 overflow-x-auto px-4">
        {items.map((item, index) => (
          <span key={`${item.label}-${index}`} className="flex shrink-0 items-center gap-2">
            {index > 0 ? <span aria-hidden="true" className="text-surface-700">/</span> : null}
            {item.to ? (
              <Link to={item.to} className="transition hover:text-surface-200">
                {t(item.label)}
              </Link>
            ) : (
              <span className="text-surface-300">{t(item.label)}</span>
            )}
          </span>
        ))}
      </div>
    </nav>
  )
}

type BreadcrumbItem = {
  label: string
  to?: string
}

function getBreadcrumbItems(pathname: string): BreadcrumbItem[] {
  if (pathname === '/dashboard') return []

  if (pathname === '/admin/squad-builder') {
    return [{ label: 'Home', to: '/dashboard' }, { label: 'Squad Builder beheer' }]
  }

  if (pathname === '/dashboard/poules') {
    return [{ label: 'Home', to: '/dashboard' }, { label: 'Voetbalpoules' }]
  }

  if (pathname === '/dashboard/manager') {
    return [{ label: 'Home', to: '/dashboard' }, { label: 'Golero Manager' }]
  }

  const managerLeagueMatch = pathname.match(/^\/dashboard\/manager\/competities\/([^/]+)$/)
  if (managerLeagueMatch) {
    return [
      { label: 'Home', to: '/dashboard' },
      { label: 'Golero Manager', to: '/dashboard/manager' },
      { label: 'Competitie' },
    ]
  }

  const managerRoundMatch = pathname.match(/^\/dashboard\/manager\/competities\/([^/]+)\/rondes\/([^/]+)$/)
  if (managerRoundMatch) {
    return [
      { label: 'Home', to: '/dashboard' },
      { label: 'Golero Manager', to: '/dashboard/manager' },
      { label: 'Competitie', to: `/dashboard/manager/competities/${managerRoundMatch[1]}` },
      { label: 'Spelerkeuze' },
    ]
  }

  if (pathname === '/dashboard/trivia') {
    return [{ label: 'Home', to: '/dashboard' }, { label: 'Golero Trivia' }]
  }

  if (pathname === '/vrienden' || pathname === '/friends') {
    return [{ label: 'Home', to: '/dashboard' }, { label: 'Vrienden' }]
  }

  if (pathname === '/profiel') {
    return [{ label: 'Home', to: '/dashboard' }, { label: 'Profiel' }]
  }

  if (pathname === '/profiel/prijzenkast') {
    return [
      { label: 'Home', to: '/dashboard' },
      { label: 'Profiel', to: '/profiel' },
      { label: 'Prijzenkast' },
    ]
  }

  const pouleMatch = pathname.match(/^\/poules\/([^/]+)(?:\/wedstrijden\/([^/]+))?$/)
  if (pouleMatch) {
    const [, pouleId, matchId] = pouleMatch
    const items: BreadcrumbItem[] = [
      { label: 'Home', to: '/dashboard' },
      { label: 'Voetbalpoules', to: '/dashboard/poules' },
      { label: 'Poule', to: `/poules/${pouleId}` },
    ]

    if (matchId) items.push({ label: 'Wedstrijd' })
    else items[items.length - 1] = { label: 'Poule' }

    return items
  }

  if (pathname.startsWith('/wedstrijden/')) {
    return [{ label: 'Home', to: '/dashboard' }, { label: 'Wedstrijd' }]
  }

  if (pathname.startsWith('/teams/')) {
    return [{ label: 'Home', to: '/dashboard' }, { label: 'Team' }]
  }

  return []
}

function DesktopHeader({
  userLabel,
  isAdmin,
  onLogout,
  theme,
  onToggleTheme,
}: {
  userLabel: string
  isAdmin: boolean
  onLogout: () => void
  theme: 'dark' | 'light'
  onToggleTheme: () => void
}) {
  const { pathname } = useLocation()
  const { t, locale, setLocale } = useTranslation()
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)
  const userMenuRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!isUserMenuOpen) return

    function handleClickOutside(event: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isUserMenuOpen])

  return (
    <header className="app-header fixed inset-x-0 top-0 z-40 hidden border-b border-surface-800/90 bg-surface-900/92 backdrop-blur-xl sm:block">
      <div className="border-b border-white/[0.045]">
        <div className="mx-auto flex w-full max-w-[1000px] items-center justify-end px-4 py-1.5">
          <div className="flex items-center gap-1">
            <ThemeToggle theme={theme} onToggle={onToggleTheme} />
            <LanguagePicker compact locale={locale} onChange={setLocale} />
            <div className="mx-1 h-4 w-px bg-white/[0.1]" aria-hidden="true" />

            <div className="relative" ref={userMenuRef}>
              <button
                type="button"
                onClick={() => setIsUserMenuOpen((open) => !open)}
                aria-label={`${t('Ingelogd als')} ${userLabel}`}
                aria-expanded={isUserMenuOpen}
                className="flex max-w-[15rem] items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold text-surface-500 transition hover:bg-white/[0.04] hover:text-surface-200"
              >
                <span className="shrink-0 leading-none">{t('Ingelogd als')}</span>
                <span className="truncate leading-none text-surface-300">{userLabel}</span>
                <span
                  aria-hidden="true"
                  className={[
                    'h-3 w-3 shrink-0 transition-transform',
                    isUserMenuOpen ? 'rotate-180' : '',
                  ].join(' ')}
                >
                  <svg viewBox="0 0 16 16" className="h-full w-full" fill="none">
                    <path d="m4 6 4 4 4-4" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
                  </svg>
                </span>
              </button>

              {isUserMenuOpen ? (
                <div className="absolute right-0 top-full z-50 mt-2 w-48 rounded-[1rem] border border-white/[0.07] bg-[#0d1113] p-2 shadow-[0_18px_40px_rgba(0,0,0,0.42)]">
                  <Link
                    to="/profiel"
                    onClick={() => setIsUserMenuOpen(false)}
                    className="block rounded-[0.75rem] px-3 py-2.5 text-sm font-medium text-surface-200 transition hover:bg-[#151c1e] hover:text-white"
                  >
                    {t('Profiel')}
                  </Link>
                  {isAdmin ? (
                    <Link
                      to="/admin/squad-builder"
                      onClick={() => setIsUserMenuOpen(false)}
                      className="block rounded-[0.75rem] px-3 py-2.5 text-sm font-medium text-surface-200 transition hover:bg-[#151c1e] hover:text-white"
                    >
                      {t('Squad Builder beheer')}
                    </Link>
                  ) : null}
                  <div className="my-1.5 h-px bg-white/[0.07]" />
                  <button
                    type="button"
                    onClick={onLogout}
                    className="block w-full rounded-[0.75rem] px-3 py-2.5 text-left text-sm font-medium text-surface-200 transition hover:bg-[#151c1e] hover:text-white"
                  >
                    {t('Uitloggen')}
                  </button>
                </div>
              ) : null}
            </div>

          </div>
        </div>
      </div>
      <div className="mx-auto w-full max-w-[1000px] px-4 py-2.5">
        <div className="flex items-center gap-6">
          <div className="flex w-full min-w-0 items-center gap-6">
            <Link to="/dashboard" className="shrink-0 transition hover:opacity-85">
              <img src={goleroLogo} alt="Golero" className="app-logo h-6 w-auto" />
            </Link>

            <nav className="app-main-nav ml-auto flex flex-wrap items-center justify-end gap-2.5">
              {appNavItems.map((item) => (
                <DesktopNavItem
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  external={item.external}
                  isActive={isAppNavItemActive(item.to, pathname)}
                >
                  {t(item.label)}
                </DesktopNavItem>
              ))}
            </nav>
          </div>
        </div>
      </div>
    </header>
  )
}

function MobileHeader({
  isMenuOpen,
  onToggleMenu,
  theme,
  onToggleTheme,
}: {
  isMenuOpen: boolean
  onToggleMenu: () => void
  theme: 'dark' | 'light'
  onToggleTheme: () => void
}) {
  const { t, locale, setLocale } = useTranslation()
  return (
    <header className="app-header fixed inset-x-0 top-0 z-40 border-b border-surface-800/90 bg-surface-900/92 backdrop-blur-xl sm:hidden">
      <div className="mx-auto flex h-[4.75rem] w-full max-w-[1000px] items-center justify-between px-4">
        <div className="min-w-0">
          <Link to="/dashboard" className="block">
            <img src={goleroLogo} alt="Golero" className="app-logo h-5 w-auto" />
          </Link>
        </div>

        <div className="flex items-center gap-1">
          <ThemeToggle theme={theme} onToggle={onToggleTheme} />
          <LanguagePicker compact locale={locale} onChange={setLocale} />
          <button
            type="button"
            aria-label={isMenuOpen ? t('Sluit menu') : t('Open menu')}
            aria-expanded={isMenuOpen}
            onClick={onToggleMenu}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full text-surface-200 transition hover:bg-white/[0.05] hover:text-white"
          >
            <span className="relative h-[0.95rem] w-[1.1rem]">
              <span
                className={[
                  'absolute left-0 top-0 h-px w-[1.1rem] rounded-full bg-current transition-all duration-300 ease-out',
                  isMenuOpen ? 'translate-y-[6px] rotate-45' : '',
                ].join(' ')}
              />
              <span
                className={[
                  'absolute left-0 top-[6px] h-px w-[1.1rem] rounded-full bg-current transition-all duration-300 ease-out',
                  isMenuOpen ? 'opacity-0' : 'opacity-100',
                ].join(' ')}
              />
              <span
                className={[
                  'absolute left-0 top-[12px] h-px w-[1.1rem] rounded-full bg-current transition-all duration-300 ease-out',
                  isMenuOpen ? '-translate-y-[6px] -rotate-45' : '',
                ].join(' ')}
              />
            </span>
          </button>
        </div>
      </div>
    </header>
  )
}

function MobileMenu({
  userLabel,
  isAdmin,
  isOpen,
  onClose,
  onLogout,
}: {
  userLabel: string
  isAdmin: boolean
  isOpen: boolean
  onClose: () => void
  onLogout: () => void
}) {
  const { pathname } = useLocation()
  const { t } = useTranslation()

  return (
    <div
      className={[
        'fixed inset-0 z-50 sm:hidden',
        isOpen ? 'pointer-events-auto' : 'pointer-events-none',
      ].join(' ')}
    >
      <button
        type="button"
        aria-label={t('Sluit menu overlay')}
        tabIndex={isOpen ? 0 : -1}
        onClick={onClose}
        className={[
          'absolute inset-0 bg-black/55 backdrop-blur-sm transition-opacity duration-300 ease-out',
          isOpen ? 'opacity-100' : 'opacity-0',
        ].join(' ')}
      />

      <div
        className={[
          'app-mobile-menu absolute right-0 top-0 flex h-full w-[min(84vw,20rem)] flex-col bg-[linear-gradient(180deg,_rgba(8,12,18,0.98),_rgba(4,7,12,0.99))] px-5 pb-6 pt-[4.35rem] shadow-[0_18px_60px_rgba(0,0,0,0.45)] transition-all duration-300 ease-out transform-gpu',
          isOpen ? 'translate-x-0 opacity-100' : 'translate-x-[8%] opacity-0',
        ].join(' ')}
      >
        <div className="px-4">
          <p className="text-[0.68rem] font-semibold uppercase tracking-[0.22em] text-surface-500/90">
            {t('Ingelogd als')}
          </p>
          <Link
            to="/profiel"
            onClick={onClose}
            className="mt-2 block truncate text-base font-semibold text-white transition hover:text-surface-200"
          >
            {userLabel}
          </Link>
          {isAdmin ? (
            <Link
              to="/admin/squad-builder"
              onClick={onClose}
              className="mt-3 block truncate text-sm font-semibold text-accent-300 transition hover:text-accent-200"
            >
              {t('Squad Builder beheer')}
            </Link>
          ) : null}
        </div>

        <div className="mx-4 mt-3 h-px bg-white/[0.08]" />

        <nav className="app-main-nav mt-4 space-y-2">
          {appNavItems.map((item, index) => (
            <MobileNavItem
              key={item.to}
              to={item.to}
              end={item.end}
              external={item.external}
              isActive={isAppNavItemActive(item.to, pathname)}
              onClick={onClose}
              isOpen={isOpen}
              delayMs={index * 45}
            >
              {t(item.label)}
            </MobileNavItem>
          ))}
        </nav>

        <div className="mt-auto space-y-3 pt-5">
          <Link
            to="/profiel"
            onClick={onClose}
            className="flex min-h-11 items-center px-4 text-sm font-medium text-surface-200 transition hover:text-white"
          >
            {t('Mijn profiel')}
          </Link>
          <button
            type="button"
            onClick={onLogout}
            className="flex min-h-11 w-full items-center px-4 text-sm font-medium text-surface-200 transition hover:text-white"
          >
            {t('Uitloggen')}
          </button>
        </div>
      </div>
    </div>
  )
}

function DesktopNavItem({
  to,
  end = false,
  external = false,
  isActive: isActiveOverride,
  children,
}: {
  to: string
  end?: boolean | undefined
  external?: boolean | undefined
  isActive: boolean
  children: ReactNode
}) {
  const className = ({ isActive }: { isActive?: boolean }) =>
    [
      'inline-flex min-h-10 items-center rounded-full px-3.5 py-2 text-[0.92rem] font-medium leading-none transition',
      (isActiveOverride || isActive)
        ? 'bg-accent-500/15 text-accent-300'
        : 'text-surface-400 hover:bg-white/[0.04] hover:text-surface-100',
    ].join(' ')

  if (external) {
    return <a href={to} className={className({})}>{children}</a>
  }

  return (
    <NavLink
      to={to}
      end={end}
      className={className}
    >
      {children}
    </NavLink>
  )
}

function MobileNavItem({
  to,
  end = false,
  external = false,
  isActive: isActiveOverride,
  children,
  onClick,
  isOpen,
  delayMs,
}: {
  to: string
  end?: boolean | undefined
  external?: boolean | undefined
  isActive: boolean
  children: ReactNode
  onClick: () => void
  isOpen: boolean
  delayMs: number
}) {
  const className = [
    'flex min-h-[3.15rem] items-center rounded-[0.95rem] px-4 text-[0.98rem] font-medium transition-all duration-300 ease-out',
    isOpen ? 'translate-x-0 opacity-100' : 'translate-x-4 opacity-0',
    (isActiveOverride)
      ? 'bg-accent-500/15 text-accent-300'
      : 'text-surface-300 hover:bg-white/[0.045] hover:text-white',
  ].join(' ')

  if (external) {
    return <a href={to} onClick={onClick} style={{ transitionDelay: `${isOpen ? delayMs : 0}ms` }} className={className}>{children}</a>
  }

  return (
    <NavLink
      to={to}
      end={end}
      onClick={onClick}
      style={{ transitionDelay: `${isOpen ? delayMs : 0}ms` }}
      className={({ isActive }) =>
        [
          'flex min-h-[3.15rem] items-center rounded-[0.95rem] px-4 text-[0.98rem] font-medium transition-all duration-300 ease-out',
          isOpen ? 'translate-x-0 opacity-100' : 'translate-x-4 opacity-0',
          (isActiveOverride || isActive)
            ? 'bg-accent-500/15 text-accent-300'
            : 'text-surface-300 hover:bg-white/[0.045] hover:text-white',
        ].join(' ')
      }
    >
      {children}
    </NavLink>
  )
}

function isAppNavItemActive(to: string, pathname: string) {
  if (to === '/dashboard') return pathname === '/dashboard'

  if (to === '/dashboard/poules') {
    return (
      pathname === '/dashboard/poules' ||
      pathname === '/poules' ||
      pathname.startsWith('/poules/')
    )
  }

  if (to === '/dashboard/manager') {
    return pathname === '/dashboard/manager' || pathname.startsWith('/dashboard/manager/')
  }

  if (to === '/dashboard/trivia') {
    return pathname === '/dashboard/trivia' || pathname.startsWith('/dashboard/trivia/')
  }

  if (to === '/vrienden') return pathname === '/vrienden' || pathname === '/friends'

  return pathname === to || pathname.startsWith(`${to}/`)
}
